<script setup lang="ts">
import {
  Check as CheckIcon,
  GalleryVerticalEnd as GalleryVerticalEndIcon,
  LoaderCircle as LoaderCircleIcon,
  Plus as PlusIcon,
  Route as RouteIcon,
} from '@lucide/vue'
import { reactive, type Component } from 'vue'

import AnimatedXButton from '@/components/ui/AnimatedXButton.vue'
import { logService, LOG_CATEGORIES } from '@/services/logService'

/**
 * The card shown when hovering/tapping a browsable basemap POI (rendered inside a MapLibre popup by
 * `PoiBrowseLayer`). Built from the shared design-system primitives.
 *
 * Information hierarchy: category icon + name (title), then type · coordinates, then any extra tags,
 * then an "Add to" destination list. A POI can be added to the **collection** (a highlight) or to a
 * specific **route / activity** (as a waypoint) — keeping utility POIs (water, shops…) off the
 * collection level so it stays a curated highlight list (the Komoot model). Mounted standalone outside
 * the app tree, so handlers are passed as plain function props.
 */
defineOptions({ name: 'PoiBrowseCard' })

export interface PoiDestination {
  id: string
  label: string
  kind: 'collection' | 'tour'
  /** Accent colour for the route/activity dot. */
  color?: string
  /** The recommended target for the current context (emphasised + listed first). */
  primary?: boolean
  /** Far from the POI — de-emphasised so the nearby (likely) targets stand out. */
  dimmed?: boolean
  alreadyAdded: boolean
  add: () => Promise<void>
}

const props = withDefaults(
  defineProps<{
    name: string
    /** Category label (e.g. "Restaurant"); only shown as the type when it differs from the name. */
    typeLabel: string
    icon: Component
    /** Category colour for the icon chip (matches the map marker). */
    iconColor: string
    coordsText: string
    tags: Array<[string, string]>
    hiddenTagCount: number
    destinations: PoiDestination[]
    close: () => void
    /** When inside a bottom sheet, drop the card chrome + its own scroll (the sheet provides both). */
    bare?: boolean
  }>(),
  { bare: false },
)

const showType = props.typeLabel.toLowerCase() !== props.name.toLowerCase()

type RowState = 'idle' | 'adding' | 'added' | 'failed'
const states = reactive<Record<string, RowState>>(
  Object.fromEntries(
    props.destinations.map((dest) => [dest.id, dest.alreadyAdded ? 'added' : 'idle']),
  ),
)

const handleAdd = async (dest: PoiDestination) => {
  if (states[dest.id] === 'adding' || states[dest.id] === 'added') return
  states[dest.id] = 'adding'
  try {
    await dest.add()
    states[dest.id] = 'added'
  } catch (error) {
    logService.error(LOG_CATEGORIES.MAP, 'Failed to add POI to destination', {
      error,
      destinationId: dest.id,
      kind: dest.kind,
    })
    states[dest.id] = 'failed'
  }
}
</script>

<template>
  <div
    :class="bare ? '' : 'w-72 max-w-[80vw] rounded-2xl bg-white p-4 shadow-xl ring-1 ring-black/5'"
    data-testid="poi-browse-card"
  >
    <!-- Identity header: pinned while the body (tags + destinations) scrolls inside the bottom
         sheet (bare mode), so the POI name and close button stay in view. -->
    <div
      class="flex items-start gap-3"
      :class="bare ? 'sticky top-0 z-10 bg-white px-4 pt-1 pb-3' : ''"
    >
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow-sm"
        :style="{ backgroundColor: iconColor }"
        aria-hidden="true"
      >
        <component :is="icon" class="h-4.5 w-4.5" :stroke-width="2.25" />
      </span>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="truncate text-sm font-semibold text-gray-900" :title="name">{{ name }}</h2>
        <p class="mt-0.5 truncate text-xs text-gray-500">
          <span v-if="showType">{{ typeLabel }} · </span>{{ coordsText }}
        </p>
      </div>
      <AnimatedXButton size="sm" aria-label="Close" @click="close" />
    </div>

    <div :class="bare ? 'px-4 pb-4' : ''">
      <dl v-if="tags.length" class="mt-3 space-y-1 border-t border-gray-100 pt-3">
        <div v-for="[key, value] in tags" :key="key" class="flex items-baseline gap-2 text-xs">
          <dt class="shrink-0 font-mono text-gray-400">{{ key }}</dt>
          <dd class="truncate text-gray-700" :title="value">{{ value }}</dd>
        </div>
        <p v-if="hiddenTagCount > 0" class="pt-0.5 text-[11px] text-gray-400">
          +{{ hiddenTagCount }} more saved with the POI
        </p>
      </dl>

      <p
        v-if="destinations.length"
        class="mt-3 mb-1 px-1 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-400"
      >
        Add to
      </p>
      <div v-if="destinations.length" class="-mx-1" :class="bare ? '' : 'max-h-56 overflow-y-auto'">
        <button
          v-for="dest in destinations"
          :key="dest.id"
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition hover:cursor-pointer disabled:cursor-default"
          :class="[
            dest.primary && states[dest.id] !== 'added'
              ? 'bg-emerald-50 ring-1 ring-emerald-200 hover:bg-emerald-100/70'
              : 'hover:bg-emerald-50/60 disabled:hover:bg-transparent',
            dest.dimmed && !dest.primary && states[dest.id] !== 'added' ? 'opacity-45' : '',
          ]"
          :disabled="states[dest.id] === 'added' || states[dest.id] === 'adding'"
          @click="handleAdd(dest)"
        >
          <span class="flex h-5 w-5 shrink-0 items-center justify-center">
            <GalleryVerticalEndIcon
              v-if="dest.kind === 'collection'"
              class="h-4 w-4 text-gray-500"
            />
            <RouteIcon v-else class="h-4 w-4" :style="{ color: dest.color ?? '#64748b' }" />
          </span>

          <span
            class="min-w-0 flex-1 truncate text-sm"
            :class="
              dest.primary && states[dest.id] !== 'added'
                ? 'font-medium text-emerald-900'
                : 'text-gray-800'
            "
          >
            {{ dest.label }}
          </span>

          <span class="shrink-0">
            <LoaderCircleIcon
              v-if="states[dest.id] === 'adding'"
              class="h-4 w-4 animate-spin text-gray-400"
            />
            <span
              v-else-if="states[dest.id] === 'added'"
              class="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"
            >
              <CheckIcon class="h-4 w-4" /> Added
            </span>
            <span v-else-if="states[dest.id] === 'failed'" class="text-xs font-medium text-red-600">
              Retry
            </span>
            <PlusIcon v-else class="h-4 w-4 text-gray-400" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
